<script setup lang="ts">
import InvoiceSummaryCard from '@/components/invoices/InvoiceSummaryCard.vue';
import CommercialDocumentDetailsSheet from '@/components/revenue/CommercialDocumentDetailsSheet.vue';
import { EmptyState } from '@/components/ui/empty-state';
import GroupedEntityList from '@/components/workspace/GroupedEntityList.vue';
import { ReceiptText } from 'lucide-vue-next';
import { clientOwnershipLabel } from '~/lib/clientOwnership';
import type { Invoice } from '~/types/models';

const props = withDefaults(defineProps<{
    invoices: Invoice[];
    showProject?: boolean;
    showClient?: boolean;
    canMarkPaid?: boolean;
    canView?: boolean;
    canEdit?: boolean;
    canSend?: boolean;
    canDelete?: boolean;
    canCopyPublicLink?: boolean;
    projectNameById?: Record<string, string>;
    clientNameById?: Record<string, string>;
    quoteNumberById?: Record<string, string>;
    tenantSlug?: string;
    emptyTitle?: string;
    emptyDescription?: string;
}>(), {
    showProject: true,
    showClient: true,
    canMarkPaid: false,
    canView: true,
    canEdit: false,
    canSend: false,
    canDelete: false,
    canCopyPublicLink: false,
    projectNameById: () => ({}),
    clientNameById: () => ({}),
    quoteNumberById: () => ({}),
    tenantSlug: '',
    emptyTitle: 'No invoices found',
    emptyDescription: 'Create an invoice or adjust the filters to see billing work here.',
});

const emit = defineEmits<{
    markPaid: [invoiceId: string];
    view: [invoice: Invoice];
    edit: [invoice: Invoice];
    send: [invoiceId: string];
    remind: [invoiceId: string];
    'copy-public-link': [invoice: Invoice];
    delete: [invoiceId: string];
}>();
const { tenantPath } = useTenantPath();

const selectedInvoice = ref<Invoice | null>(null);
const detailsOpen = computed({
    get: () => Boolean(selectedInvoice.value),
    set: (value: boolean) => {
        if (!value) selectedInvoice.value = null;
    },
});

function openDetails(invoice: Invoice) {
    selectedInvoice.value = invoice;
}

function viewSelectedInvoice() {
    if (!selectedInvoice.value) return;
    emit('view', selectedInvoice.value);
    selectedInvoice.value = null;
}

function editSelectedInvoice() {
    if (!selectedInvoice.value) return;
    emit('edit', selectedInvoice.value);
    selectedInvoice.value = null;
}

function copySelectedInvoiceLink() {
    if (!selectedInvoice.value) return;
    emit('copy-public-link', selectedInvoice.value);
}

function quoteHref(quoteId: string): string {
    return `${tenantPath('/quotes')}?focusQuoteId=${encodeURIComponent(quoteId)}`;
}

function clientLabel(invoice: Invoice): string {
    return clientOwnershipLabel(invoice, {
        clientNameById: props.clientNameById,
    });
}
</script>

<template>
    <GroupedEntityList :empty="invoices.length === 0">
        <template #empty>
            <EmptyState :title="emptyTitle" :description="emptyDescription">
                <template #icon><ReceiptText class="size-8" /></template>
            </EmptyState>
        </template>

        <InvoiceSummaryCard
            v-for="invoice in invoices"
            :key="invoice.id"
            :invoice="invoice"
            :show-project="showProject"
            :show-client="showClient"
            :can-mark-paid="canMarkPaid"
            :can-view="canView"
            :can-edit="canEdit"
            :can-send="canSend"
            :can-delete="canDelete"
            :can-copy-public-link="canCopyPublicLink"
            :project-name="projectNameById[invoice.projectId || ''] || ''"
            :client-name="clientLabel(invoice)"
            :quote-number="quoteNumberById[invoice.quoteId || ''] || ''"
            :quote-href="invoice.quoteId ? quoteHref(invoice.quoteId) : ''"
            surface="list"
            @mark-paid="emit('markPaid', $event)"
            @view="emit('view', $event)"
            @details="openDetails"
            @edit="emit('edit', $event)"
            @send="emit('send', $event)"
            @remind="emit('remind', $event)"
            @copy-public-link="emit('copy-public-link', $event)"
            @delete="emit('delete', $event)"
        />
    </GroupedEntityList>

    <CommercialDocumentDetailsSheet
        v-if="selectedInvoice"
        v-model:open="detailsOpen"
        kind="invoice"
        :invoice="selectedInvoice"
        :client-name="clientLabel(selectedInvoice)"
        :project-name="projectNameById[selectedInvoice.projectId || ''] || ''"
        :quote-number="quoteNumberById[selectedInvoice.quoteId || ''] || ''"
        :quote-href="selectedInvoice.quoteId ? quoteHref(selectedInvoice.quoteId) : ''"
        :can-view="canView"
        :can-edit="canEdit"
        :can-copy-public-link="canCopyPublicLink"
        @view="viewSelectedInvoice"
        @edit="editSelectedInvoice"
        @copy-public-link="copySelectedInvoiceLink"
    />
</template>
